import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp,
  Plus,
  Search,
  MoreVertical,
  Eye,
  CheckCircle2,
  Clock,
  Upload,
  Sparkles,
  BookOpen,
  Globe,
  Filter,
  GraduationCap,
  ArrowRight,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  Building2,
  Briefcase
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  getDashboardAnalytics, 
  getStudents, 
  createStudent,
  getApplications,
  createApplication,
  getCourses,
  getUniversities,
  getCommissions,
  checkEligibility,
  quickEligibilityCheck,
  getMarketInsights,
  uploadDocument,
  getStudentDocuments,
  getActiveAgentsCount
} from '@/services/api';
import { toast } from 'sonner';

// Stats Card
const StatsCard = ({ title, value, icon, subtext, color = 'primary' }) => {
  const colorClasses = {
    primary: 'text-[#00D4FF] bg-[#00D4FF]/10',
    accent: 'text-[#8B5CF6] bg-[#8B5CF6]/10',
    success: 'text-[#10B981] bg-[#10B981]/10',
    warning: 'text-[#F59E0B] bg-[#F59E0B]/10',
  };

  return (
    <Card className="bg-[#141029] border-[#2D2654]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-[#6B678A] mb-1">{title}</p>
            <p className="text-2xl font-semibold text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {value}
            </p>
            {subtext && <p className="text-xs text-[#6B678A] mt-1">{subtext}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Application Stepper Component
const ApplicationStepper = ({ currentStatus }) => {
  const steps = [
    { key: 'submitted', label: 'Submitted' },
    { key: 'under_review', label: 'Under Review' },
    { key: 'loa_issued', label: 'LOA Issued' },
    { key: 'visa_filed', label: 'Visa Filed' },
    { key: 'enrolled', label: 'Enrolled' }
  ];

  const currentIndex = steps.findIndex(s => s.key === currentStatus);

  return (
    <div className="flex items-center justify-between w-full" data-testid="application-stepper">
      {steps.map((step, index) => {
        const isComplete = index < currentIndex;
        const isActive = index === currentIndex;
        const isPending = index > currentIndex;

        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  isComplete ? 'bg-[#10B981] text-white' :
                  isActive ? 'bg-[#00D4FF] text-[#0F172A] shadow-[0_0_20px_rgba(0,229,255,0.4)]' :
                  'bg-[#2D2654] text-[#6B678A]'
                }`}
              >
                {isComplete ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
              </div>
              <span className={`text-xs mt-2 ${isActive ? 'text-[#00D4FF]' : 'text-[#6B678A]'}`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${index < currentIndex ? 'bg-[#10B981]' : 'bg-[#2D2654]'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Agent Overview
const AgentOverview = () => {
  const [stats, setStats] = useState(null);
  const [recentApps, setRecentApps] = useState([]);
  const [activeAgents, setActiveAgents] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [statsData, appsData, agentsData] = await Promise.all([
        getDashboardAnalytics(),
        getApplications(),
        getActiveAgentsCount()
      ]);
      setStats(statsData);
      setRecentApps(appsData.slice(0, 5));
      setActiveAgents(agentsData.active_agents || 0);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00D4FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statusColors = {
    submitted: 'bg-[#3B82F6]/20 text-[#60A5FA]',
    under_review: 'bg-[#F59E0B]/20 text-[#FBBF24]',
    loa_issued: 'bg-[#10B981]/20 text-[#34D399]',
    enrolled: 'bg-[#00D4FF]/20 text-[#00D4FF]',
    rejected: 'bg-[#EF4444]/20 text-[#F87171]'
  };

  return (
    <div className="space-y-6" data-testid="agent-overview">
      {/* Live Agents Banner */}
      <div className="bg-gradient-to-r from-[#00D4FF]/10 to-[#8B5CF6]/10 border border-[#2D2654] rounded-xl p-4 flex items-center justify-between" data-testid="live-agents-banner">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-[#10B981] rounded-full animate-pulse" />
          <span className="text-[#B8B4CE] text-sm">
            <span className="text-white font-semibold text-lg">{activeAgents}</span> agents are live on portal
          </span>
        </div>
        <Users className="w-5 h-5 text-[#00D4FF]" />
      </div>

      <div>
        <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Welcome Back!
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">Here's your recruitment performance overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Students"
          value={stats?.students_count || 0}
          icon={<Users className="w-5 h-5" strokeWidth={1.5} />}
          color="primary"
        />
        <StatsCard
          title="Active Applications"
          value={stats?.pending_applications || 0}
          icon={<FileText className="w-5 h-5" strokeWidth={1.5} />}
          color="warning"
        />
        <StatsCard
          title="Approved (LOA)"
          value={stats?.approved_applications || 0}
          icon={<CheckCircle2 className="w-5 h-5" strokeWidth={1.5} />}
          color="success"
        />
        <StatsCard
          title="Total Earnings"
          value={`$${(stats?.total_earnings || 0).toLocaleString()}`}
          icon={<DollarSign className="w-5 h-5" strokeWidth={1.5} />}
          subtext={`$${(stats?.pending_earnings || 0).toLocaleString()} pending`}
          color="accent"
        />
      </div>

      {/* Recent Applications */}
      <Card className="bg-[#141029] border-[#2D2654]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-[white] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Recent Applications
          </CardTitle>
          <Button variant="ghost" className="text-[#00D4FF] hover:text-[#00B8D4]">
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentApps.map((app) => (
              <div key={app.id} className="p-4 rounded-lg bg-[#0D0B1E] border border-[#2D2654]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-[white]">{app.student_name}</p>
                    <p className="text-xs text-[#6B678A]">{app.course_name} • {app.university_name}</p>
                  </div>
                  <Badge className={statusColors[app.status] || 'bg-[#2D2654] text-[#B8B4CE]'}>
                    {app.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <ApplicationStepper currentStatus={app.status} />
              </div>
            ))}
            {recentApps.length === 0 && (
              <p className="text-center text-[#6B678A] py-8">No applications yet. Start by adding a student!</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Students Management
const AgentStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStudent, setNewStudent] = useState({
    first_name: '', last_name: '', email: '', phone: '', nationality: '',
    gpa: '', ielts_score: '', toefl_score: '', education_level: '', budget_min: '', budget_max: ''
  });

  const loadStudents = useCallback(async () => {
    try {
      const data = await getStudents();
      setStudents(data);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const handleAddStudent = async () => {
    try {
      await createStudent({
        ...newStudent,
        gpa: newStudent.gpa ? parseFloat(newStudent.gpa) : null,
        ielts_score: newStudent.ielts_score ? parseFloat(newStudent.ielts_score) : null,
        toefl_score: newStudent.toefl_score ? parseInt(newStudent.toefl_score) : null,
        budget_min: newStudent.budget_min ? parseFloat(newStudent.budget_min) : null,
        budget_max: newStudent.budget_max ? parseFloat(newStudent.budget_max) : null,
      });
      toast.success('Student added successfully');
      setShowAddModal(false);
      setNewStudent({ first_name: '', last_name: '', email: '', phone: '', nationality: '', gpa: '', ielts_score: '', toefl_score: '', education_level: '', budget_min: '', budget_max: '' });
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-students">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            My Students
          </h1>
          <p className="text-[#6B678A] text-sm mt-1">Manage your student leads</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]" data-testid="add-student-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141029] border-[#2D2654] text-[white] max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add New Student</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="First Name"
                  value={newStudent.first_name}
                  onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                  data-testid="student-first-name"
                />
                <Input
                  placeholder="Last Name"
                  value={newStudent.last_name}
                  onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                  data-testid="student-last-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Email"
                  type="email"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
                <Input
                  placeholder="Phone"
                  value={newStudent.phone}
                  onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
              </div>
              <Input
                placeholder="Nationality"
                value={newStudent.nationality}
                onChange={(e) => setNewStudent({ ...newStudent, nationality: e.target.value })}
                className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
              />
              <div className="grid grid-cols-3 gap-4">
                <Input
                  placeholder="GPA (e.g., 3.5)"
                  type="number"
                  step="0.1"
                  value={newStudent.gpa}
                  onChange={(e) => setNewStudent({ ...newStudent, gpa: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
                <Input
                  placeholder="IELTS Score"
                  type="number"
                  step="0.5"
                  value={newStudent.ielts_score}
                  onChange={(e) => setNewStudent({ ...newStudent, ielts_score: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
                <Input
                  placeholder="TOEFL Score"
                  type="number"
                  value={newStudent.toefl_score}
                  onChange={(e) => setNewStudent({ ...newStudent, toefl_score: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
              </div>
              <Select 
                value={newStudent.education_level} 
                onValueChange={(value) => setNewStudent({ ...newStudent, education_level: value })}
              >
                <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-[white]">
                  <SelectValue placeholder="Education Level" />
                </SelectTrigger>
                <SelectContent className="bg-[#141029] border-[#2D2654]">
                  <SelectItem value="high_school" className="text-[white]">High School</SelectItem>
                  <SelectItem value="bachelors" className="text-[white]">Bachelor's Degree</SelectItem>
                  <SelectItem value="masters" className="text-[white]">Master's Degree</SelectItem>
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Budget Min (USD)"
                  type="number"
                  value={newStudent.budget_min}
                  onChange={(e) => setNewStudent({ ...newStudent, budget_min: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
                <Input
                  placeholder="Budget Max (USD)"
                  type="number"
                  value={newStudent.budget_max}
                  onChange={(e) => setNewStudent({ ...newStudent, budget_max: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
              </div>
              <Button 
                onClick={handleAddStudent} 
                className="w-full bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]"
                data-testid="submit-student-btn"
              >
                Add Student
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Students Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {students.map((student, index) => (
          <Card key={student.id} className="bg-[#141029] border-[#2D2654] hover:border-[#00D4FF]/30 transition-all" data-testid={`student-card-${index}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#00D4FF]/20 flex items-center justify-center text-[#00D4FF] font-medium text-lg">
                    {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-[white]">{student.first_name} {student.last_name}</p>
                    <p className="text-xs text-[#6B678A]">{student.nationality}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#6B678A]">GPA</span>
                  <span className="text-[white]">{student.gpa || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6B678A]">IELTS</span>
                  <span className="text-[white]">{student.ielts_score || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6B678A]">Budget</span>
                  <span className="text-[white]">
                    ${student.budget_min?.toLocaleString() || '0'} - ${student.budget_max?.toLocaleString() || 'N/A'}
                  </span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#2D2654] flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 border-[#2D2654] text-[#B8B4CE] hover:bg-[#1A1435]">
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
                <Button size="sm" className="flex-1 bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]">
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {students.length === 0 && !loading && (
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-[#6B678A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#6B678A]">No students yet. Click "Add Student" to get started.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Course Search with Smart Filters
const AgentCourses = () => {
  const [courses, setCourses] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const PAGE_SIZE = 20;
  const [filters, setFilters] = useState({
    country: '',
    degree_level: '',
    field_of_study: '',
    max_tuition: '',
    min_tuition: '',
    ielts_score: '',
    intake_month: '',
    co_op: '',
    scholarship: '',
    university_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Eligibility check state
  const [eligibilityModal, setEligibilityModal] = useState(null); // course object
  const [eligibilityForm, setEligibilityForm] = useState({ gpa: '', ielts_score: '', toefl_score: '', pte_score: '', duolingo_score: '', budget: '', nationality: '' });
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  const INTAKE_MONTHS = [
    { value: '1', label: 'January' }, { value: '2', label: 'February' },
    { value: '5', label: 'May' }, { value: '7', label: 'July' },
    { value: '8', label: 'August' }, { value: '9', label: 'September' }
  ];

  const fetchUniversities = useCallback(() => {
    getUniversities({ limit: 200 }).then(setUniversities).catch(() => {});
  }, []);

  useEffect(() => { fetchUniversities(); }, [fetchUniversities]);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (filters.country) params.country = filters.country;
      if (filters.degree_level) params.degree_level = filters.degree_level;
      if (filters.field_of_study) params.field_of_study = filters.field_of_study;
      if (filters.max_tuition) params.max_tuition = parseFloat(filters.max_tuition);
      if (filters.min_tuition) params.min_tuition = parseFloat(filters.min_tuition);
      if (filters.ielts_score) params.ielts_score = parseFloat(filters.ielts_score);
      if (filters.intake_month) params.intake_month = parseInt(filters.intake_month);
      if (filters.co_op === 'true') params.co_op = true;
      if (filters.scholarship === 'true') params.scholarship = true;
      if (filters.university_id) params.university_id = filters.university_id;
      if (searchText.trim()) params.search = searchText.trim();
      
      const data = await getCourses(params);
      setCourses(data.courses || data);
      const total = data.total_count || data.length || 0;
      const pages = data.total_pages || Math.ceil(total / PAGE_SIZE) || 1;
      setTotalCount(total);
      setTotalPages(pages);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [page, filters, searchText]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  const handleSearch = () => {
    setPage(0);
    loadCourses();
  };

  const clearFilters = () => {
    setFilters({ country: '', degree_level: '', field_of_study: '', max_tuition: '', min_tuition: '', ielts_score: '', intake_month: '', co_op: '', scholarship: '', university_id: '' });
    setSearchText('');
    setPage(0);
    setTimeout(loadCourses, 0);
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length + (searchText ? 1 : 0);

  const monthName = (m) => ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m] || '';

  const filteredUnis = filters.country
    ? universities.filter(u => u.country === filters.country)
    : universities;

  return (
    <div className="space-y-5" data-testid="agent-courses">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Programs & Courses
          </h1>
          <p className="text-[#6B678A] text-sm mt-1">Browse 2,300+ programs across 218 institutions in 4 countries</p>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B678A]" />
              <Input
                placeholder="Search programs, universities..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-[#0D0B1E] border-[#2D2654] text-white pl-10"
                data-testid="course-search-input"
              />
            </div>
            <Button onClick={() => setShowFilters(!showFilters)} variant="outline" className="border-[#2D2654] text-[#B8B4CE] hover:bg-[#1E1A3A]" data-testid="toggle-filters-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filters {activeFilterCount > 0 && <Badge className="ml-2 bg-[#00D4FF] text-[#0F172A] text-xs px-1.5">{activeFilterCount}</Badge>}
            </Button>
            <Button onClick={handleSearch} className="bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]" data-testid="search-courses-btn">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#2D2654] space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Select value={filters.country} onValueChange={(v) => setFilters({ ...filters, country: v, university_id: '' })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="country-filter">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="Canada" className="text-white">Canada</SelectItem>
                    <SelectItem value="United Kingdom" className="text-white">United Kingdom</SelectItem>
                    <SelectItem value="United States" className="text-white">United States</SelectItem>
                    <SelectItem value="Australia" className="text-white">Australia</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.degree_level} onValueChange={(v) => setFilters({ ...filters, degree_level: v })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="degree-filter">
                    <SelectValue placeholder="Degree Level" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="diploma" className="text-white">Diploma</SelectItem>
                    <SelectItem value="bachelors" className="text-white">Bachelor's</SelectItem>
                    <SelectItem value="masters" className="text-white">Master's</SelectItem>
                    <SelectItem value="phd" className="text-white">PhD</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.field_of_study} onValueChange={(v) => setFilters({ ...filters, field_of_study: v })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="field-filter">
                    <SelectValue placeholder="Field of Study" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="engineering" className="text-white">Engineering & IT</SelectItem>
                    <SelectItem value="business" className="text-white">Business & Commerce</SelectItem>
                    <SelectItem value="health" className="text-white">Health & Medicine</SelectItem>
                    <SelectItem value="arts" className="text-white">Arts & Humanities</SelectItem>
                    <SelectItem value="science" className="text-white">Science</SelectItem>
                    <SelectItem value="hospitality" className="text-white">Hospitality & Tourism</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.intake_month} onValueChange={(v) => setFilters({ ...filters, intake_month: v })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="intake-filter">
                    <SelectValue placeholder="Intake Month" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    {INTAKE_MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value} className="text-white">{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Select value={filters.university_id} onValueChange={(v) => setFilters({ ...filters, university_id: v })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="university-filter">
                    <SelectValue placeholder="University" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654] max-h-60">
                    {filteredUnis.map(u => (
                      <SelectItem key={u.id} value={u.id} className="text-white text-xs">{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Max Tuition"
                  type="number"
                  value={filters.max_tuition}
                  onChange={(e) => setFilters({ ...filters, max_tuition: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-white"
                  data-testid="max-tuition-filter"
                />
                <Input
                  placeholder="Min IELTS"
                  type="number"
                  step="0.5"
                  value={filters.ielts_score}
                  onChange={(e) => setFilters({ ...filters, ielts_score: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-white"
                  data-testid="ielts-filter"
                />

                <div className="flex gap-2">
                  <Select value={filters.co_op} onValueChange={(v) => setFilters({ ...filters, co_op: v })}>
                    <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white">
                      <SelectValue placeholder="Co-op" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141029] border-[#2D2654]">
                      <SelectItem value="true" className="text-white">Co-op Available</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={clearFilters} variant="ghost" className="text-[#6B678A] hover:text-white text-sm" data-testid="clear-filters-btn">
                  <X className="w-3 h-3 mr-1" /> Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      {!loading && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#6B678A]">
            Showing <span className="text-white font-medium">{courses.length}</span> of <span className="text-white font-medium">{totalCount}</span> programs
            <span> — Page {page + 1} of {totalPages}</span>
          </p>
        </div>
      )}

      {/* Course Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={`skeleton-${i}`} className="bg-[#141029] border-[#2D2654] animate-pulse">
              <CardContent className="p-5 h-48" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {courses.map((course, index) => (
            <Card key={course.id} className="bg-[#141029] border-[#2D2654] hover:border-[#00D4FF]/30 transition-all group" data-testid={`course-card-${index}`}>
              <CardContent className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 mr-3">
                    <p className="font-medium text-white group-hover:text-[#00D4FF] transition-colors">{course.name}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Building2 className="w-3 h-3 text-[#6B678A]" />
                      <p className="text-sm text-[#6B678A]">{course.university_name}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-[#00D4FF]/20 text-[#00D4FF] capitalize text-xs">{course.degree_level}</Badge>
                    {course.field_of_study && <Badge className="bg-[#8B5CF6]/15 text-[#8B5CF6] capitalize text-xs">{course.field_of_study}</Badge>}
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-y-2 text-sm mb-3">
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-[#10B981]" />
                    <span className="text-[#10B981] font-medium">{course.currency} {course.tuition_fee?.toLocaleString()}</span>
                    <span className="text-[#6B678A] text-xs">/yr</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-[#6B678A]" />
                    <span className="text-white">{course.duration_months} months</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <GraduationCap className="w-3.5 h-3.5 text-[#6B678A]" />
                    <span className="text-[#B8B4CE]">GPA: {course.min_gpa || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-[#6B678A]" />
                    <span className="text-[#B8B4CE]">IELTS: {course.min_ielts || 'N/A'}</span>
                  </div>
                </div>

                {/* Intake & Tags */}
                <div className="flex items-center flex-wrap gap-1.5 mb-3">
                  <Calendar className="w-3.5 h-3.5 text-[#6B678A]" />
                  {course.intake_months?.map(m => (
                    <span key={m} className="px-2 py-0.5 bg-[#1E1A3A] text-[#B8B4CE] text-xs rounded">{monthName(m)}</span>
                  ))}
                  {course.co_op_available && <Badge className="bg-[#10B981]/15 text-[#10B981] text-xs ml-1">Co-op</Badge>}
                  {course.scholarship_available && <Badge className="bg-[#F59E0B]/15 text-[#F59E0B] text-xs">Scholarship</Badge>}
                </div>

                {/* Extra scores */}
                {(course.min_toefl || course.min_pte || course.min_duolingo) && (
                  <div className="flex gap-3 text-xs text-[#6B678A] mb-3">
                    {course.min_toefl && <span>TOEFL: {course.min_toefl}</span>}
                    {course.min_pte && <span>PTE: {course.min_pte}</span>}
                    {course.min_duolingo && <span>Duolingo: {course.min_duolingo}</span>}
                    {course.application_fee && <span>App Fee: {course.currency} {course.application_fee}</span>}
                  </div>
                )}

                <Button onClick={() => { setEligibilityModal(course); setEligibilityResult(null); setEligibilityForm({ gpa: '', ielts_score: '', toefl_score: '', pte_score: '', duolingo_score: '', budget: '', nationality: '' }); }} className="w-full bg-[#00D4FF]/10 text-[#00D4FF] hover:bg-[#00D4FF]/20 border border-[#00D4FF]/30" data-testid={`apply-btn-${index}`}>
                  <Sparkles className="w-4 h-4 mr-2" /> Check Eligibility
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && courses.length > 0 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="border-[#2D2654] text-[#B8B4CE] hover:bg-[#1E1A3A]"
            data-testid="prev-page-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-[#6B678A]">Page <span className="text-white font-medium">{page + 1}</span> of <span className="text-white font-medium">{totalPages}</span></span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page + 1 >= totalPages}
            className="border-[#2D2654] text-[#B8B4CE] hover:bg-[#1E1A3A]"
            data-testid="next-page-btn"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {courses.length === 0 && !loading && (
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-[#6B678A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white font-medium mb-1">No programs found</p>
            <p className="text-[#6B678A] text-sm">Try adjusting your filters or search terms</p>
          </CardContent>
        </Card>
      )}

      {/* Eligibility Check Modal */}
      {eligibilityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="eligibility-modal">
          <div className="bg-[#141029] border border-[#2D2654] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Check Eligibility</h2>
                  <p className="text-sm text-[#00D4FF] mt-1">{eligibilityModal.name}</p>
                  <p className="text-xs text-[#6B678A] flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" /> {eligibilityModal.university_name}</p>
                </div>
                <button onClick={() => setEligibilityModal(null)} className="text-[#6B678A] hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              {/* Course Requirements Summary */}
              <div className="bg-[#0D0B1E] rounded-lg p-4 mb-5 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-[#6B678A]">Tuition:</span> <span className="text-[#10B981] font-medium">{eligibilityModal.currency} {eligibilityModal.tuition_fee?.toLocaleString()}/yr</span></div>
                <div><span className="text-[#6B678A]">Duration:</span> <span className="text-white">{eligibilityModal.duration_months} months</span></div>
                <div><span className="text-[#6B678A]">Min GPA:</span> <span className="text-white">{eligibilityModal.min_gpa || 'N/A'}</span></div>
                <div><span className="text-[#6B678A]">Min IELTS:</span> <span className="text-white">{eligibilityModal.min_ielts || 'N/A'}</span></div>
                <div><span className="text-[#6B678A]">Min TOEFL:</span> <span className="text-white">{eligibilityModal.min_toefl || 'N/A'}</span></div>
                <div><span className="text-[#6B678A]">Min PTE:</span> <span className="text-white">{eligibilityModal.min_pte || 'N/A'}</span></div>
              </div>

              {!eligibilityResult ? (
                <>
                  <p className="text-sm text-[#B8B4CE] mb-4">Enter student profile to check eligibility:</p>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div>
                      <label className="text-xs text-[#6B678A] mb-1 block">GPA (out of 4.0)</label>
                      <Input type="number" step="0.1" max="4.0" placeholder="e.g. 3.5" value={eligibilityForm.gpa} onChange={e => setEligibilityForm({...eligibilityForm, gpa: e.target.value})} className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="elig-gpa" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B678A] mb-1 block">IELTS Score</label>
                      <Input type="number" step="0.5" max="9.0" placeholder="e.g. 7.0" value={eligibilityForm.ielts_score} onChange={e => setEligibilityForm({...eligibilityForm, ielts_score: e.target.value})} className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="elig-ielts" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B678A] mb-1 block">TOEFL Score</label>
                      <Input type="number" placeholder="e.g. 95" value={eligibilityForm.toefl_score} onChange={e => setEligibilityForm({...eligibilityForm, toefl_score: e.target.value})} className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="elig-toefl" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B678A] mb-1 block">PTE Score</label>
                      <Input type="number" placeholder="e.g. 60" value={eligibilityForm.pte_score} onChange={e => setEligibilityForm({...eligibilityForm, pte_score: e.target.value})} className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="elig-pte" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B678A] mb-1 block">Duolingo Score</label>
                      <Input type="number" placeholder="e.g. 115" value={eligibilityForm.duolingo_score} onChange={e => setEligibilityForm({...eligibilityForm, duolingo_score: e.target.value})} className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="elig-duolingo" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B678A] mb-1 block">Annual Budget ({eligibilityModal.currency})</label>
                      <Input type="number" placeholder="e.g. 40000" value={eligibilityForm.budget} onChange={e => setEligibilityForm({...eligibilityForm, budget: e.target.value})} className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="elig-budget" />
                    </div>
                  </div>
                  <Button
                    onClick={async () => {
                      setCheckingEligibility(true);
                      try {
                        const payload = { course_id: eligibilityModal.id };
                        if (eligibilityForm.gpa) payload.gpa = parseFloat(eligibilityForm.gpa);
                        if (eligibilityForm.ielts_score) payload.ielts_score = parseFloat(eligibilityForm.ielts_score);
                        if (eligibilityForm.toefl_score) payload.toefl_score = parseInt(eligibilityForm.toefl_score);
                        if (eligibilityForm.pte_score) payload.pte_score = parseInt(eligibilityForm.pte_score);
                        if (eligibilityForm.duolingo_score) payload.duolingo_score = parseInt(eligibilityForm.duolingo_score);
                        if (eligibilityForm.budget) payload.budget = parseFloat(eligibilityForm.budget);
                        const result = await quickEligibilityCheck(payload);
                        setEligibilityResult(result);
                      } catch (err) { toast.error('Eligibility check failed'); }
                      finally { setCheckingEligibility(false); }
                    }}
                    disabled={checkingEligibility || (!eligibilityForm.gpa && !eligibilityForm.ielts_score)}
                    className="w-full bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4] disabled:opacity-50"
                    data-testid="run-eligibility-btn"
                  >
                    {checkingEligibility ? <><Clock className="w-4 h-4 mr-2 animate-spin" /> Checking...</> : <><Sparkles className="w-4 h-4 mr-2" /> Check Eligibility</>}
                  </Button>
                </>
              ) : (
                <div className="space-y-4" data-testid="eligibility-result">
                  {/* Score */}
                  <div className="text-center py-4">
                    <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full border-4 ${eligibilityResult.eligibility_percentage >= 70 ? 'border-[#10B981]' : eligibilityResult.eligibility_percentage >= 50 ? 'border-[#F59E0B]' : 'border-[#EF4444]'}`}>
                      <span className="text-3xl font-bold text-white">{Math.round(eligibilityResult.eligibility_percentage)}%</span>
                    </div>
                    <p className="text-[#B8B4CE] text-sm mt-3">{eligibilityResult.analysis}</p>
                  </div>

                  {/* Strengths */}
                  {eligibilityResult.strengths?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-[#10B981] mb-2 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Strengths</h3>
                      <ul className="space-y-1">{eligibilityResult.strengths.map((s, i) => <li key={`str-${s.slice(0,20)}`} className="text-sm text-[#B8B4CE] pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-[#10B981] before:rounded-full">{s}</li>)}</ul>
                    </div>
                  )}

                  {/* Concerns */}
                  {eligibilityResult.concerns?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-[#F59E0B] mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> Concerns</h3>
                      <ul className="space-y-1">{eligibilityResult.concerns.map((c, i) => <li key={`con-${c.slice(0,20)}`} className="text-sm text-[#B8B4CE] pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-[#F59E0B] before:rounded-full">{c}</li>)}</ul>
                    </div>
                  )}

                  {/* Recommendations */}
                  {eligibilityResult.recommendations?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-[#8B5CF6] mb-2 flex items-center gap-1"><Sparkles className="w-4 h-4" /> Recommendations</h3>
                      <ul className="space-y-1">{eligibilityResult.recommendations.map((r, i) => <li key={`rec-${r.slice(0,20)}`} className="text-sm text-[#B8B4CE] pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-[#8B5CF6] before:rounded-full">{r}</li>)}</ul>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => setEligibilityResult(null)} variant="outline" className="flex-1 border-[#2D2654] text-[#B8B4CE] hover:bg-[#1E1A3A]" data-testid="recheck-btn">Re-check</Button>
                    <Button onClick={() => setEligibilityModal(null)} className="flex-1 bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]">Close</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const AgentApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadApplications = useCallback(async () => {
    try {
      const data = await getApplications();
      setApplications(data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const statusColors = {
    submitted: 'bg-[#3B82F6]/20 text-[#60A5FA]',
    under_review: 'bg-[#F59E0B]/20 text-[#FBBF24]',
    documents_required: 'bg-[#EF4444]/20 text-[#F87171]',
    loa_issued: 'bg-[#10B981]/20 text-[#34D399]',
    visa_filed: 'bg-[#8B5CF6]/20 text-[#A78BFA]',
    enrolled: 'bg-[#00D4FF]/20 text-[#00D4FF]',
    rejected: 'bg-[#EF4444]/20 text-[#F87171]'
  };

  return (
    <div className="space-y-6" data-testid="agent-applications">
      <div>
        <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Applications
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">Track your student applications</p>
      </div>

      <div className="space-y-4">
        {applications.map((app, index) => (
          <Card key={app.id} className="bg-[#141029] border-[#2D2654]" data-testid={`application-card-${index}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-medium text-[white]">{app.student_name}</p>
                  <p className="text-sm text-[#6B678A]">{app.course_name}</p>
                  <p className="text-xs text-[#6B678A]">{app.university_name}</p>
                </div>
                <Badge className={statusColors[app.status] || 'bg-[#2D2654] text-[#B8B4CE]'}>
                  {app.status?.replace('_', ' ')}
                </Badge>
              </div>
              <ApplicationStepper currentStatus={app.status} />
              <div className="mt-4 pt-4 border-t border-[#2D2654] flex justify-between items-center">
                <p className="text-xs text-[#6B678A]">
                  Applied: {new Date(app.created_at).toLocaleDateString()}
                </p>
                <Button variant="ghost" size="sm" className="text-[#00D4FF]">
                  View Details <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {applications.length === 0 && !loading && (
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-[#6B678A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#6B678A]">No applications yet. Start by searching for courses!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Commissions Page
const AgentCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCommissions = useCallback(async () => {
    try {
      const data = await getCommissions();
      setCommissions(data);
    } catch (error) {
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCommissions();
  }, [loadCommissions]);

  const totalPaid = commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.amount, 0);
  const totalPending = commissions.filter(c => c.status !== 'paid').reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="space-y-6" data-testid="agent-commissions">
      <div>
        <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Commission Ledger
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">Track your earnings and payouts</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatsCard
          title="Total Paid"
          value={`$${totalPaid.toLocaleString()}`}
          icon={<CheckCircle2 className="w-5 h-5" strokeWidth={1.5} />}
          color="success"
        />
        <StatsCard
          title="Pending Payout"
          value={`$${totalPending.toLocaleString()}`}
          icon={<Clock className="w-5 h-5" strokeWidth={1.5} />}
          color="warning"
        />
      </div>

      {/* Commissions Table */}
      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0D0B1E] border-b border-[#2D2654]">
                <tr>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Student</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">University</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Amount</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Status</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((comm) => (
                  <tr key={comm.id} className="border-b border-[#2D2654] hover:bg-[#1A1435] transition-colors">
                    <td className="px-6 py-4 text-sm text-[white]">{comm.student_name}</td>
                    <td className="px-6 py-4 text-sm text-[#B8B4CE]">{comm.university_name}</td>
                    <td className="px-6 py-4 text-sm font-medium text-[#8B5CF6]">
                      ${comm.amount.toLocaleString()} {comm.currency}
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={
                        comm.status === 'paid' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        comm.status === 'approved' ? 'bg-[#F59E0B]/20 text-[#FBBF24]' :
                        'bg-[#2D2654] text-[#B8B4CE]'
                      }>
                        {comm.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#6B678A]">
                      {new Date(comm.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {commissions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-[#6B678A]">
                      No commissions yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Market Insights
const AgentInsights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadInsights = useCallback(async () => {
    try {
      const data = await getMarketInsights();
      setInsights(data);
    } catch (error) {
      toast.error('Failed to load market insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00D4FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agent-insights">
      <div>
        <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Market Insights
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">Data-driven analytics to boost your recruitment</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsCard
          title="Visa Success Rate"
          value={`${insights?.visa_success_rate || 0}%`}
          icon={<TrendingUp className="w-5 h-5" strokeWidth={1.5} />}
          color="success"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Countries */}
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardHeader>
            <CardTitle className="text-[white] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Top Destinations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {insights?.top_countries?.slice(0, 5).map((country, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#0D0B1E] border border-[#2D2654] flex items-center justify-center">
                      <Globe className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.5} />
                    </div>
                    <span className="text-[white]">{country._id}</span>
                  </div>
                  <span className="text-[#B8B4CE]">{country.count} applications</span>
                </div>
              ))}
              {(!insights?.top_countries || insights.top_countries.length === 0) && (
                <p className="text-[#6B678A] text-center py-4">No data available yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trending Courses */}
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardHeader>
            <CardTitle className="text-[white] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trending Courses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {insights?.trending_courses?.slice(0, 5).map((course, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#0D0B1E] border border-[#2D2654] flex items-center justify-center">
                      <GraduationCap className="w-4 h-4 text-[#8B5CF6]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-[white] text-sm">{course.course_name}</p>
                      <p className="text-[#6B678A] text-xs">{course.university_name}</p>
                    </div>
                  </div>
                  <span className="text-[#B8B4CE] text-sm">{course.count} apps</span>
                </div>
              ))}
              {(!insights?.trending_courses || insights.trending_courses.length === 0) && (
                <p className="text-[#6B678A] text-center py-4">No data available yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Agent Dashboard
const AgentDashboard = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<AgentOverview />} />
        <Route path="/students" element={<AgentStudents />} />
        <Route path="/applications" element={<AgentApplications />} />
        <Route path="/courses" element={<AgentCourses />} />
        <Route path="/commissions" element={<AgentCommissions />} />
        <Route path="/insights" element={<AgentInsights />} />
      </Routes>
    </DashboardLayout>
  );
};

export default AgentDashboard;
